import styled, { css } from 'styled-components';
import { zIndex } from 'styles';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { BORDER_BOX } from 'common';

const {
  COLORS,
  SPACING,
  RADIUS,
  TYPOGRAPHY,
  FONT_WEIGHT,
  TRANSITION,
  SPEED,
  LINE_HEIGHT,
} = Tokens;

const TAB_MIN_WIDTH_CONDENSED = '4.5rem';
const TAB_MIN_WIDTH = '5.5rem';
const TAB_MIN_WIDTH_LARGE = '7rem';
const TAB_MAX_WIDTH = '22.5rem';
const TAB_HEIGHT = '3rem';
const VERTICAL_TAB_HEIGHT = '3.5rem';
const BORDER_WIDTH = '0.125rem';

const BREAKPOINT_7PLUS = '@media only screen and (min-width: 768px)'; // (Breakpoints 7 and above)

/* General Variables */
const TRANSITION_BEZIER = TRANSITION.TRANSITION_DECELERATE;
const TRANSITION_TIME = SPEED.SPEED_NORMAL;
const BUTTON_TYPOGRAPHY = {
  ...TYPOGRAPHY.BODY,
  lineHeight: LINE_HEIGHT.LINE_HEIGHT_NONE,
  fontWeight: FONT_WEIGHT.FONT_WEIGHT_BOLD,
  textTransform: 'uppercase',
};

const TabsSelectedIndicator = styled.div`
  ${BORDER_BOX}
  ${({ vertical, theme }) => css`${!vertical ? 'border-bottom: ' : 'border-right: '} ${theme.on.onIndicator} ${SPACING.SPACING_4} solid;`}
  position: absolute;
  bottom: 0;
  ${({ disabled }) => disabled && css`
    border-bottom: ${({ theme }) => theme.disabled.onIndicator} ${SPACING.SPACING_4} solid;
  `}
`;

const TabCloseButton = styled.div`
  box-sizing: content-box;
  padding-left: ${SPACING.SPACING_12};
  opacity: 0;
  position: absolute;
  width: 1.25rem;
  height: 100%;
  right: -1.5625rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transform: translateX(-1.8125rem);
  ${({ rtl }) => rtl && css`
    right: inherit;
    left: -1.5625rem;
    transform: translateX(1.8125rem);
    padding-right: ${SPACING.SPACING_12};
    padding-left: 0;
  `}
  ${({ $disableAnimation }) => !$disableAnimation &&
  css`
    transition: opacity ${TRANSITION_TIME} ${TRANSITION_BEZIER};
  `}
`;

const TabNotificationBadge = styled.div`
  height: 100%;
  &:before {
    content: '';
    position: absolute;
    top: 50%;
    right:auto;
    transform: translateY(-50%);
    height: 0.625rem;
    width: 0.625rem;
    border-radius: ${RADIUS.RADIUS_CIRCLE};
    ${({ rtl }) => rtl && css`
      margin-right: 0.375rem;
    `}
    ${({ rtl }) => !rtl && css`
      margin-left: 0.375rem;;
    `}
    background-color: ${({ theme }) => theme && theme.notification};
  }
`;

const TabIcon = styled.div`
  svg {
    & * {
      fill: ${({ theme }) => theme && theme.off.content};
    }
    ${({ $disableAnimation }) => !$disableAnimation &&
    css`
      transition: fill ${TRANSITION_TIME} ${TRANSITION_BEZIER};
    `}
  }
  ${({ selected }) => selected && css`
      svg {
        & * {
          fill: ${({ theme }) => theme && theme.on.content};
        }
      }
  `}
  ${({ disabled }) => disabled && css`
    svg {
      & * {
        fill: ${({ theme }) => theme && theme.disabled.content};
      }
    }
  `}
`;

const TabLabel = styled.div`
  position: relative; 
  ${BUTTON_TYPOGRAPHY}
  color: ${({ theme }) => theme && theme.off.content};
  text-align: center;
  ${({ $disableAnimation }) => !$disableAnimation &&
  css`
    transition: color ${TRANSITION_TIME} ${TRANSITION_BEZIER};
  `}
  white-space: normal;
  text-align: center;
  ${({ selected, $isPressed }) => selected && !$isPressed && css`
    color: ${({ theme }) => theme && theme.on.content};
  `}
  // ${({ $isPressed }) => $isPressed && css`
  //   color: ${({ theme }) => theme && theme.on.pressedText};
  // `}
  ${({ disabled }) => disabled && css`
    color: ${({ theme }) => theme && theme.disabled.content};
    background-color:  ${({ theme }) => theme && theme.disabled.background};
  `}
  /** HorizontalNavigation for backward compatibility */
  ${({ variant }) => (variant === 'horizontal-navigation') && css`
    color: ${({ theme }) => theme.off.content};
    ${({ selected, $isPressed }) => selected && !$isPressed && css`
      color: ${({ theme }) => theme && theme.on.content};
    `}
    /* disabled */
    ${({ disabled }) => disabled && css`
      color: ${({ theme }) => theme.disabled && theme.disabled.content};
        cursor: default;
        &:hover {
          color: ${({ theme }) => theme.disabled && theme.disabled.content};
        }
    `}
  `}
`;

const Tab = styled.div`
  ${BORDER_BOX}
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  padding: 0 ${SPACING.SPACING_12};
  margin: 0;
  outline: 0;
  overflow: hidden;
  ${BREAKPOINT_7PLUS} {
    padding: 0 ${props => !props.vertical && props.condensed ? SPACING.SPACING_12 : SPACING.SPACING_24};
  }
`;

const TabWrapper = styled.div`
  ${BORDER_BOX}
  border: none;
  ${({ vertical, divider, theme }) => vertical ?
    css`
      width: 4.375rem;
      height: ${VERTICAL_TAB_HEIGHT};
      ${divider && `border-bottom: 0.0625rem solid ${theme && theme.bottomBorder};`}
    `
    :
    css`max-width: ${TAB_MAX_WIDTH};
    min-width: ${props => props.condensed ? TAB_MIN_WIDTH_CONDENSED : TAB_MIN_WIDTH};
    ${BREAKPOINT_7PLUS} {
      min-width: ${props => props.condensed ? TAB_MIN_WIDTH_CONDENSED : TAB_MIN_WIDTH_LARGE};
    }
    height: ${TAB_HEIGHT};
    background-color: ${theme.off.background};`}
  outline: none;
  padding: 0;
  &::-moz-focus-inner { border: 0 }
  /** unselected style*/
  ${({ selected, vertical, closeButton, rtl, theme }) => !selected && css`
    &:hover {
      color: ${theme.off.hovered.content};
      background-color: ${theme.off.hovered.background};
      ${TabNotificationBadge} {
        opacity: ${closeButton && vertical ? 0 : 1}
      }
      ${TabCloseButton} {
        opacity: 1;
        z-index: ${zIndex.TAB_CLOSE_BUTTON};
        ${({ $disableAnimation }) => !$disableAnimation &&
        css`
          transition: opacity ${TRANSITION_TIME} ${TRANSITION_BEZIER};
        `}
        ${!vertical && `background-image: linear-gradient(to ${rtl ? 'left' : 'right'},${COLORS.TRANSPARENT} 0%, ${theme && theme.closeButton.hover} 30%);`}
      }
      ${TabLabel} {
        /* for backward compatibility */
        ${({ variant }) => (variant === 'horizontal-navigation') && css`
          color: ${theme.off.content};
          background-color: ${COLORS.TRANSPARENT};
        `} 
      }
    }
    /** Focus indicator  */
    &:focus, &:focus-within {
      color: ${theme.off.focused.content};
      background-color: ${theme.off.focused.background};
      box-shadow: inset 0 0 0 ${BORDER_WIDTH} ${theme.off.focused.border};
      &:hover {
        color: ${theme.off.focused.hovered.content};
        background-color: ${!vertical ? theme.off.focused.hovered.background : 'transparent'};
        ${TabNotificationBadge} {
          opacity: ${closeButton && vertical ? 0 : 1}
        }
        ${TabCloseButton} {
          ${!vertical && `background-image: linear-gradient(to ${rtl ? 'left' : 'right'},${COLORS.TRANSPARENT} 0%, ${theme && theme.closeButton.hover} 30%);`}
        }
      }
    }
  `}

  /** selected style*/
  ${({ selected, vertical, closeButton, rtl, theme }) => selected && css`
    color: ${theme.on.content};
    background-color: ${!vertical ? theme.on.background : 'transparent'};
    &:focus, &:focus-within {
      color: ${theme.on.focused.content};
      background-color: ${!vertical ? theme.on.focused.background : 'transparent'};
      border: none;
      box-shadow: inset 0 0 0 ${BORDER_WIDTH} ${theme.on.focused.border};
      &:hover {
        color: ${theme.on.focused.hovered.content};
        background-color: ${!vertical ? theme.off.focused.hovered.background : 'transparent'};
        ${TabNotificationBadge} {
          opacity: ${closeButton && vertical ? 0 : 1}
        }
        ${TabCloseButton} {
          ${!vertical && `background-image: linear-gradient(to ${rtl ? 'left' : 'right'},${COLORS.TRANSPARENT} 0%, ${theme && theme.closeButton.hover} 30%);`}
        }
      }
      /* for backward compatibility */
      ${({ variant }) => (variant === 'horizontal-navigation') && css`
        ${TabLabel} {
          color: ${theme.on.focused.content};
        }
      `} 
    }
    &:hover {
      color: ${theme.on.hovered.content};
      background-color: ${theme.on.hovered.background};
      /* for backward compatibility */
      ${({ variant }) => (variant === 'horizontal-navigation') && css`
        ${TabLabel} {
            color: ${theme.on.focused.content};
            background-color: ${COLORS.TRANSPARENT}
        }
      `} 
      ${TabNotificationBadge} {
        opacity: ${closeButton && vertical ? 0 : 1}
      }
      ${TabCloseButton} {
        opacity: 1;
        z-index: ${zIndex.TAB_CLOSE_BUTTON};
        ${({ $disableAnimation }) => !$disableAnimation &&
        css`
          transition: opacity ${TRANSITION_TIME} ${TRANSITION_BEZIER};
        `}
        ${vertical ? css`height: calc(100% - 0.25rem);` : css`height: calc(100% - 0.5rem);`}
        ${!vertical && `background-image: linear-gradient(to ${rtl ? 'left' : 'right'},${COLORS.TRANSPARENT} 0%, ${theme && theme.closeButton.hover} 30%);`}
      }
    }
  `}

  /* disabled */
  ${({ disabled }) => disabled && css`
    pointer-events: none;
    color: ${({ theme }) => theme.disabled.content};
    background-color: ${({ theme }) => theme.disabled.background};
    cursor: default;
  `}

  /** CloseButton */
  &:focus, &:focus-within {
    ${TabCloseButton} {
      z-index: ${zIndex.TAB_CLOSE_BUTTON};
      opacity: 1;
      ${({ vertical }) => vertical ? css`height: calc(100% - 0.25rem);` : css`height: calc(100% - 0.5rem);`}
    }
    ${({ closeButton, vertical }) => css`
      ${TabNotificationBadge} {
        opacity: ${closeButton && vertical ? 0 : 1}
      }
    `}
  }

  &:focus {
  ${({ selected }) => !selected && css`
    ${TabIcon} {
        svg {
          & * {
            fill: ${({ theme }) => theme.off.content};
          }
        }
      }
  `}
  };
`;

const TabsInner = styled.div`
    position: relative;
    display: flex;
    ${({ vertical }) => vertical ? css`flex-direction: column;` : css`flex-direction: row;`}
    white-space: nowrap;
    box-sizing: border-box;
    overflow-x: auto;
    overflow-y: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
      height: 0;
      width: 0;
    }
    justify-content: flex-start;
    /* Full width */
    ${({ fullWidth }) => fullWidth && css`
      flex: 1 1 auto;
      ${TabWrapper} {
        flex: 0 1 auto;
        max-width: ${TAB_MAX_WIDTH};
        min-width: ${TAB_MIN_WIDTH};
        ${BREAKPOINT_7PLUS} {
          min-width: ${TAB_MIN_WIDTH_LARGE};
        }
        width: 100%;
      }
    `}
`;

const Tabs = styled.div`    
  display: flex;
  overflow: hidden;
  min-height: ${TAB_HEIGHT};
  -webkit-overflow-scrolling: touch;
  background-color: ${({ theme }) => theme && theme.off.background};
  border-bottom: 0.0625rem solid ${({ theme }) => theme && theme.bottomBorder};
  position: relative;
  justify-content: center;
  ${({ alignment }) => alignment === 'right' && css`
    justify-content: flex-end;
  `}
  ${({ alignment }) => alignment === 'left' && css`
    justify-content: flex-start;
  `}
  ${({ rtl }) => rtl && css`
      direction: rtl;
  `}
`;

const VerticalTabs = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: fit-content;
  justify-content: center;
  position: relative;
  min-height: ${TAB_HEIGHT};
  -webkit-overflow-scrolling: touch;
  background-color: ${({ theme }) => theme && theme.off.background};
  ${({ theme, rtl }) => css`${rtl ? 'border-right: ' : 'border-left: '} 0.0625rem solid ${theme && theme.bottomBorder};`}
  ${({ rtl }) => rtl && css`direction: rtl;`}
  ${({ alignment }) => alignment === 'bottom' && css`
    justify-content: flex-end;
  `}
  ${({ alignment }) => alignment === 'top' && css`
    justify-content: flex-start;
  `}
`;

const TabScrollButtonContainer = styled.div`
  ${BORDER_BOX}
    background-color ${({ theme }) => theme && theme.off.background};
    z-index: 1;
    display: flex;
    align-items: center;
    background-image: linear-gradient(to left,${COLORS.TRANSPARENT} 0%, ${({ theme }) => theme && theme.off.background} 30%);
    ${({ right }) => right && css`
      background-image: linear-gradient(to right,${COLORS.TRANSPARENT} 0%, ${({ theme }) => theme && theme.off.background} 30%);
    `}
    ${({ vertical }) => vertical ?
    css`
      flex-shrink: 0;
      height: 2.75rem;
      display: flex;
      justify-content: center;`
    :
    css`
      height: ${TAB_HEIGHT};
      padding-left: ${SPACING.SPACING_12};
      padding-right: ${SPACING.SPACING_12};`
}
`;

const TabsContent = styled.div`
  position: relative;
  overflow: hidden;
  ${({ rtl }) => rtl && css`
    direction: rtl;
  `}
`;

const styles = {
  Tabs,
  VerticalTabs,
  TabsInner,
  TabWrapper,
  Tab,
  TabIcon,
  TabsSelectedIndicator,
  TabNotificationBadge,
  TabCloseButton,
  TabScrollButtonContainer,
  TabsContent,
  TabLabel,
  BUTTON_TYPOGRAPHY,
  TRANSITION_BEZIER,
  TRANSITION_TIME,
};

export default styles;
